// Universal tap-to-hear (Read Mode) — kid-safe read-aloud for pre-readers.
//
// Two modes:
//   • Seedlings (4-6, tier="seedlings"): STRICT double-tap confirm.
//       – First tap on a button/link/tile → speaks the label + shows an
//         "audio-wave" pulse ring on the element. Navigation is BLOCKED.
//       – Second tap on the SAME element within 2s → allowed through.
//       – Different element / >2s window → resets. This prevents pre-readers
//         from wandering off into pages they cannot navigate back from.
//   • Explorers + Pioneers (readers): non-blocking side-effect narration.
//       – Tap speaks the label; click still fires the action immediately.
//
// Read Mode is a persistent parent toggle stored in localStorage (survives
// reload). A global "we-read-mode-changed" event lets any pressed chip in
// the header/menu re-render without prop drilling.

import { speak, stopSpeaking } from "@/lib/speech";

const LS_KEY = "we_read_mode_v1";
const AGE_KEY = "we_age_group";
const DOUBLE_TAP_WINDOW_MS = 2000;
const CSS_ARMED = "we-tap-armed";

let installed = false;
let enabled = false;
let lastSpeakAt = 0;
let armedEl = null;
let armedAt = 0;
let armedTimer = null;

export function isReadModeOn() {
  try {
    return localStorage.getItem(LS_KEY) === "1";
  } catch {
    return false;
  }
}

export function setReadMode(on) {
  try {
    if (on) localStorage.setItem(LS_KEY, "1");
    else localStorage.removeItem(LS_KEY);
  } catch {
    // ignore quota
  }
  enabled = !!on;
  // Toggling off clears any pending arming so nothing lingers on the page.
  if (!enabled) disarm();
  try { stopSpeaking(); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent("we-read-mode-changed", { detail: { on: !!on } }));
}

// Elements we consider "readable" on tap.
const READABLE_TAGS = new Set(["BUTTON", "A", "LABEL", "SUMMARY"]);
const HEADING_TAGS = new Set(["H1", "H2", "H3", "H4"]);

function looksReadable(el) {
  if (!el || !(el instanceof Element)) return false;
  if (el.hasAttribute("data-no-speak")) return false;
  if (el.hasAttribute("data-speak")) return true;
  if (el.getAttribute("role") === "button" || el.getAttribute("role") === "link") return true;
  if (READABLE_TAGS.has(el.tagName)) return true;
  if (HEADING_TAGS.has(el.tagName)) return true;
  // Inputs/textarea we skip — we don't want to read out user-typed text.
  return false;
}

function isInteractive(el) {
  // For the double-tap gate we only intercept elements the child would
  // actually navigate away with (buttons + links + role=button/link).
  if (!el) return false;
  if (el.hasAttribute("data-no-tap-confirm")) return false;
  if (READABLE_TAGS.has(el.tagName)) return true;
  const r = el.getAttribute("role");
  return r === "button" || r === "link";
}

function extractLabel(el) {
  if (!el) return "";
  const explicit = el.getAttribute("data-speak");
  if (explicit) return explicit;
  const aria = el.getAttribute("aria-label");
  if (aria) return aria;
  const title = el.getAttribute("title");
  const text = (el.textContent || "").trim().replace(/\s+/g, " ");
  if (text && text.length >= 2) return text.slice(0, 90);
  if (title) return title;
  return "";
}

function currentTier() {
  try {
    const id = localStorage.getItem(AGE_KEY);
    if (id === "little") return "seedlings";
    if (id === "big") return "explorers";
    if (id === "older") return "pioneers";
  } catch { /* ignore */ }
  return null;
}

function isSeedlingsMode() {
  return enabled && currentTier() === "seedlings";
}

function disarm() {
  if (armedEl) {
    try {
      armedEl.classList.remove(CSS_ARMED);
      armedEl.removeAttribute("data-tap-armed");
    } catch { /* ignore */ }
    armedEl = null;
  }
  armedAt = 0;
  if (armedTimer) {
    clearTimeout(armedTimer);
    armedTimer = null;
  }
}

function arm(el) {
  disarm();
  armedEl = el;
  armedAt = Date.now();
  try {
    el.classList.add(CSS_ARMED);
    el.setAttribute("data-tap-armed", "1");
  } catch { /* ignore */ }
  armedTimer = setTimeout(() => {
    // Silent timeout — just clear the pulse so the child sees the ring vanish.
    disarm();
  }, DOUBLE_TAP_WINDOW_MS);
}

function findReadable(target) {
  let el = target;
  for (let depth = 0; el && depth < 6; depth += 1) {
    if (looksReadable(el)) return el;
    el = el.parentElement;
  }
  return null;
}

// Capture-phase click handler — runs BEFORE the target's own onClick, so
// we can prevent navigation on the first tap in Seedlings mode.
function onClickCapture(e) {
  if (!enabled) return;
  const el = findReadable(e.target);
  if (!el) return;

  if (isSeedlingsMode() && isInteractive(el)) {
    // Second tap on same armed element within window → let it through.
    // Use isSameNode() (with === fallback) instead of strict reference
    // equality because React may reconcile the DOM node between the two
    // taps — the RENDERED node stays the same visually, but the JS
    // reference we captured on the 1st tap can go stale, causing a
    // strict === check to spuriously fail (see iter-32 test report).
    const isSame = armedEl && (armedEl === el || (typeof armedEl.isSameNode === "function" && armedEl.isSameNode(el)));
    if (isSame && (Date.now() - armedAt) < DOUBLE_TAP_WINDOW_MS) {
      disarm();
      return; // do NOT prevent — the tap should now actually fire.
    }
    // First tap: swallow the click, speak, arm the visual pulse.
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === "function") {
      e.stopImmediatePropagation();
    }
    const label = extractLabel(el);
    if (label) speak(label, { voice: "nova" });
    arm(el);
    return;
  }

  // Non-seedlings / non-interactive readable (e.g. heading): side-effect narration.
  const label = extractLabel(el);
  if (!label) return;
  const now = Date.now();
  if (now - lastSpeakAt < 250) return; // debounce
  lastSpeakAt = now;
  speak(label, { voice: "nova" });
}

/**
 * Install the global listener exactly once. Safe to call from a top-level
 * component's mount effect; subsequent calls are no-ops.
 */
export function installTapToHear() {
  if (installed) return;
  if (typeof document === "undefined") return;
  installed = true;
  enabled = isReadModeOn();
  // Capture=true so we run BEFORE React's synthetic-event delegation.
  document.addEventListener("click", onClickCapture, true);
  window.addEventListener("we-read-mode-changed", (e) => {
    enabled = !!e?.detail?.on;
    if (!enabled) disarm();
  });
  // Tier changes should also drop any pending armed state.
  window.addEventListener("we-age-changed", () => disarm());
}
